import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, ArrowRight, Info, RefreshCw, AlertCircle, Coins, Zap, Scale, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import ShareCard from './ShareCard';
import AnimatedCounter from '../ui/AnimatedCounter';
import ProgressRing from '../ui/ProgressRing';
import ScenarioChart from '../ui/ScenarioChart';
import GoldVisualizer from '../ui/GoldVisualizer';
import { Skeleton, PriceSkeleton } from '../ui/SkeletonLoader';
import {
  calculateGoldInvestment,
  fetchMarketData,
  fetchScenarioRates,
  CURRENCIES,
  HORIZONS,
  SCENARIOS,
} from '../../services/api';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { type: "spring", stiffness: 200, damping: 20 }
  }
};

// Instant price card component - Dark theme with animations
const InstantPriceCard = ({ goldPriceEUR, fxRates, isLoading, t }) => {
  const currencies = [
    { code: 'EUR', symbol: '€', flag: '🇪🇺' },
    { code: 'USD', symbol: '$', flag: '🇺🇸' },
    { code: 'TRY', symbol: '₺', flag: '🇹🇷' },
    { code: 'AED', symbol: 'د.إ', flag: '🇦🇪' },
  ];

  const getPriceInCurrency = (currencyCode) => {
    if (!goldPriceEUR || !fxRates) return null;
    const rate = fxRates[currencyCode] || 1;
    return (goldPriceEUR * rate).toFixed(2);
  };

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-4">
        <motion.div
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <Coins className="w-5 h-5 text-[#D4AF37]" />
        </motion.div>
        <h3 className="text-lg font-semibold text-white">{t('goldPricePerGram')}</h3>
        {isLoading && <RefreshCw className="w-4 h-4 animate-spin text-[#D4AF37]" />}
      </div>
      
      <motion.div 
        className="grid grid-cols-2 md:grid-cols-4 gap-3"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        {currencies.map((curr, index) => {
          const price = getPriceInCurrency(curr.code);
          return (
            <motion.div
              key={curr.code}
              variants={fadeInUp}
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="group bg-[#12121a] border border-[#1e1e2e] hover:border-[#D4AF37]/30 rounded-xl p-4 text-center cursor-pointer transition-colors"
            >
              {isLoading ? (
                <PriceSkeleton />
              ) : (
                <>
                  <motion.div 
                    className="text-2xl mb-1"
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {curr.flag}
                  </motion.div>
                  <div className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">
                    {curr.code}
                  </div>
                  <div className="text-xl font-bold text-white">
                    <span className="text-[#D4AF37]">{curr.symbol}</span>
                    <AnimatedCounter value={price || 0} decimals={2} delay={index * 0.1} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{t('perGram')}</div>
                </>
              )}
            </motion.div>
          );
        })}
      </motion.div>
      
      <motion.p 
        className="text-xs text-gray-500 text-center mt-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <Zap className="w-3 h-3 inline mr-1 text-[#D4AF37]" />
        {t('liveRates')} • {t('updatesEvery10Min')}
      </motion.p>
    </div>
  );
};

// Quick converter component - Dark theme with animations
const QuickConverter = ({ goldPriceEUR, fxRates, isLoading, t }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [isFocused, setIsFocused] = useState(false);
  
  const currencies = [
    { code: 'EUR', symbol: '€', name: 'EUR' },
    { code: 'USD', symbol: '$', name: 'USD' },
    { code: 'TRY', symbol: '₺', name: 'TRY' },
    { code: 'AED', symbol: 'د.إ', name: 'AED' },
  ];

  const getGoldGrams = () => {
    if (!amount || !goldPriceEUR || !fxRates || parseFloat(amount) <= 0) return null;
    const rate = fxRates[currency] || 1;
    const priceInCurrency = goldPriceEUR * rate;
    const grams = parseFloat(amount) / priceInCurrency;
    return grams.toFixed(4);
  };

  const selectedCurrency = currencies.find(c => c.code === currency);
  const grams = getGoldGrams();

  return (
    <motion.div 
      className={`mb-8 p-5 bg-[#12121a] border rounded-2xl transition-all duration-300 ${
        isFocused ? 'border-[#D4AF37]/50 shadow-lg shadow-[#D4AF37]/10' : 'border-[#1e1e2e]'
      }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <div className="flex items-center gap-2 mb-4">
        <ArrowRight className="w-5 h-5 text-[#D4AF37]" />
        <h3 className="text-base font-semibold text-white">{t('quickConverter')}</h3>
      </div>
      
      <div className="flex flex-col sm:flex-row items-center gap-3">
        {/* Amount Input */}
        <div className="flex-1 w-full sm:w-auto">
          <div className="relative">
            <Input
              type="number"
              placeholder={t('enterAmount')}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="h-12 text-lg pr-20 bg-[#0a0a0f] border-[#1e1e2e] text-white placeholder:text-gray-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 transition-all"
              min="0"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="h-8 w-20 border-0 bg-[#1e1e2e] text-sm font-medium text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#12121a] border-[#1e1e2e]">
                  {currencies.map((curr) => (
                    <SelectItem key={curr.code} value={curr.code} className="text-white hover:bg-[#1e1e2e]">
                      {curr.symbol} {curr.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Equals Sign */}
        <motion.div 
          className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] font-bold"
          animate={{ rotate: grams ? [0, 360] : 0 }}
          transition={{ duration: 0.5 }}
        >
          =
        </motion.div>

        {/* Result */}
        <div className="flex-1 w-full sm:w-auto">
          <AnimatePresence mode="wait">
            <motion.div 
              key={grams || 'empty'}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="h-12 px-4 bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-xl flex items-center justify-center sm:justify-start gap-2"
            >
              {isLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin text-[#D4AF37]" />
              ) : grams ? (
                <>
                  <span className="text-2xl font-bold text-[#D4AF37]">{grams}</span>
                  <span className="text-sm text-gray-400">{t('gramsOfGold')}</span>
                </>
              ) : (
                <span className="text-gray-500 text-sm">{t('enterAmountToConvert')}</span>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Instant calculation note */}
      <AnimatePresence>
        {grams && amount && (
          <motion.p 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="text-xs text-gray-500 mt-3 text-center sm:text-left overflow-hidden"
          >
            {selectedCurrency?.symbol}{parseFloat(amount).toLocaleString()} {currency} = <span className="font-semibold text-[#D4AF37]">{grams}g</span> gold at {selectedCurrency?.symbol}{(goldPriceEUR * (fxRates[currency] || 1)).toFixed(2)}/{t('gram')}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Results Panel Component
const ResultsPanel = ({ result, resultRef, t }) => {
  const growthPercentage = ((parseFloat(result.futureValueSelected) - result.investmentAmount) / result.investmentAmount * 100).toFixed(1);
  
  return (
    <motion.div 
      ref={resultRef}
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Main Results Card */}
      <motion.div variants={scaleIn}>
        <Card className="bg-gradient-to-br from-[#12121a] to-[#0a0a0f] border-[#1e1e2e] rounded-2xl overflow-hidden relative">
          {/* Gold accent line */}
          <motion.div 
            className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4AF37] via-[#F5ECD7] to-[#D4AF37]"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          />
          
          <CardHeader className="border-b border-[#1e1e2e]">
            <CardTitle className="text-xl font-semibold text-white flex items-center gap-2">
              <Scale className="w-5 h-5 text-[#D4AF37]" />
              {t('yourGoldInvestment')}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-6">
            {/* Two-column layout on larger screens */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Gold Amount & Visualization */}
              <div className="space-y-4">
                {/* Gold Amount */}
                <motion.div 
                  className="p-4 bg-[#0a0a0f] rounded-xl border border-[#D4AF37]/20"
                  variants={fadeInUp}
                >
                  <p className="text-sm text-gray-400 mb-1">{t('todayYouCouldBuy')}</p>
                  <p className="text-3xl md:text-4xl font-bold text-white">
                    <AnimatedCounter value={result.gramsOfGold} decimals={4} duration={1.2} />
                    <span className="text-lg md:text-xl text-gray-400 ml-2">{t('grams')}</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    {t('with')} {result.currency.symbol}{result.investmentAmount.toLocaleString()} {result.currency.code}
                  </p>
                </motion.div>

                {/* Gold Bar Visualization */}
                <motion.div 
                  className="p-4 bg-[#0a0a0f] rounded-xl border border-[#1e1e2e] flex justify-center"
                  variants={fadeInUp}
                >
                  <GoldVisualizer grams={parseFloat(result.gramsOfGold)} />
                </motion.div>
              </div>

              {/* Right: Future Value with Progress Ring */}
              <div className="space-y-4">
                {/* Future Value with Progress Ring */}
                <motion.div 
                  className="p-4 bg-gradient-to-r from-[#D4AF37]/10 to-transparent rounded-xl border border-[#D4AF37]/20"
                  variants={fadeInUp}
                >
                  <div className="flex items-center gap-4">
                    <ProgressRing 
                      progress={Math.min(parseFloat(growthPercentage), 100)} 
                      size={80}
                      strokeWidth={6}
                    >
                      <div className="text-center">
                        <span className="text-lg font-bold text-[#D4AF37]">+{growthPercentage}%</span>
                      </div>
                    </ProgressRing>
                    
                    <div className="flex-1">
                      <p className="text-sm text-gray-300 mb-1">
                        {t('afterYears')} {result.horizonLabel}
                      </p>
                      <p className="text-2xl md:text-3xl font-bold text-[#D4AF37]">
                        {result.currency.symbol}
                        <AnimatedCounter 
                          value={parseFloat(result.futureValueSelected)} 
                          decimals={2} 
                          duration={1.5}
                          delay={0.3}
                        />
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {result.scenario.label} {t('scenario')} ({result.annualRate}%/{t('year')})
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Mini Scenario Range */}
                <motion.div 
                  className="p-3 bg-[#0a0a0f] rounded-xl border border-[#1e1e2e]"
                  variants={fadeInUp}
                >
                  <p className="text-xs text-gray-500 mb-2">{t('estimatedRange')}</p>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-center flex-1">
                      <p className="text-xs text-gray-500">{t('conservative')}</p>
                      <p className="text-sm font-semibold text-gray-400">
                        {result.currency.symbol}{parseFloat(result.futureValueMin).toLocaleString()}
                      </p>
                    </div>
                    <div className="h-8 w-px bg-[#1e1e2e]" />
                    <div className="text-center flex-1">
                      <p className="text-xs text-[#D4AF37]">{t('selected')}</p>
                      <p className="text-sm font-bold text-[#D4AF37]">
                        {result.currency.symbol}{parseFloat(result.futureValueSelected).toLocaleString()}
                      </p>
                    </div>
                    <div className="h-8 w-px bg-[#1e1e2e]" />
                    <div className="text-center flex-1">
                      <p className="text-xs text-gray-500">{t('optimistic')}</p>
                      <p className="text-sm font-semibold text-emerald-400">
                        {result.currency.symbol}{parseFloat(result.futureValueMax).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Scenario Comparison Chart */}
            <motion.div 
              className="mt-6 p-4 bg-[#0a0a0f] rounded-xl border border-[#1e1e2e]"
              variants={fadeInUp}
            >
              <ScenarioChart result={result} />
            </motion.div>

            {/* Assumptions */}
            <motion.div 
              className="mt-4 p-4 bg-[#0a0a0f] rounded-xl text-sm border border-[#1e1e2e]"
              variants={fadeInUp}
            >
              <p className="font-medium text-gray-300 mb-3">{t('calculationDetails')}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-gray-400">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500">{t('goldPrice')}</span>
                  <span className="font-medium text-white">€{result.goldPriceUsed}/g</span>
                </div>
                {result.currency.code !== 'EUR' && (
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500">{t('fxRate')}</span>
                    <span className="font-medium text-white">{result.fxRateUsed}</span>
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500">{t('annualGrowth')}</span>
                  <span className="font-medium text-[#D4AF37]">{result.annualRate}%</span>
                </div>
              </div>
            </motion.div>

            {/* Disclaimer */}
            <motion.div 
              className="mt-4 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-xl"
              variants={fadeInUp}
            >
              <p className="text-xs text-yellow-200/70">
                <strong className="text-yellow-400">Disclaimer:</strong> {t('disclaimer')}
              </p>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Shareable Card */}
      <motion.div variants={fadeInUp}>
        <ShareCard result={result} />
      </motion.div>
    </motion.div>
  );
};

// Empty State Component
const EmptyState = ({ t }) => (
  <Card className="bg-[#12121a] border-[#1e1e2e] h-full min-h-[400px] flex items-center justify-center rounded-2xl">
    <CardContent className="text-center p-8">
      <motion.div 
        className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center"
        animate={{ 
          boxShadow: [
            '0 0 0 0 rgba(212, 175, 55, 0.4)',
            '0 0 20px 5px rgba(212, 175, 55, 0.2)',
            '0 0 0 0 rgba(212, 175, 55, 0.4)'
          ]
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <Calculator className="w-8 h-8 text-[#D4AF37]" />
      </motion.div>
      <h3 className="text-lg font-semibold text-white mb-2">
        {t('readyToCalculate')}
      </h3>
      <p className="text-gray-400 max-w-sm">
        {t('enterDetailsToSee')}
      </p>
    </CardContent>
  </Card>
);

const GoldCalculator = () => {
  const { t } = useLanguage();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [horizon, setHorizon] = useState('1');
  const [scenario, setScenario] = useState('base');
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [marketData, setMarketData] = useState(null);
  const [isLoadingMarket, setIsLoadingMarket] = useState(true);
  const [error, setError] = useState(null);
  const [scenarios, setScenarios] = useState(SCENARIOS);
  const resultRef = useRef(null);

  // Fetch market data and scenario rates on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingMarket(true);
        const [marketResult, ratesResult] = await Promise.all([
          fetchMarketData(),
          fetchScenarioRates()
        ]);
        setMarketData(marketResult);
        
        // Update scenarios with fetched rates
        if (ratesResult?.rates) {
          const updatedScenarios = [
            { value: 'conservative', label: 'Conservative', rate: `${(ratesResult.rates.conservative * 100).toFixed(0)}%/year` },
            { value: 'base', label: 'Base', rate: `${(ratesResult.rates.base * 100).toFixed(0)}%/year` },
            { value: 'optimistic', label: 'Optimistic', rate: `${(ratesResult.rates.optimistic * 100).toFixed(0)}%/year` }
          ];
          setScenarios(updatedScenarios);
        }
        
        setError(null);
      } catch (err) {
        console.error('Failed to load market data:', err);
        setError('Unable to fetch live market data. Using fallback values.');
      } finally {
        setIsLoadingMarket(false);
      }
    };
    
    loadData();
  }, []);

  const handleCalculate = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) return;

    setIsCalculating(true);
    setError(null);

    try {
      const calcResult = await calculateGoldInvestment(
        amount,
        currency,
        parseFloat(horizon),
        scenario
      );
      
      setResult(calcResult);

      // Scroll to result
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      console.error('Calculation error:', err);
      setError('Failed to calculate. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  }, [amount, currency, horizon, scenario]);

  const resetCalculator = () => {
    setAmount('');
    setCurrency('EUR');
    setHorizon('1');
    setScenario('base');
    setResult(null);
    setError(null);
  };

  const goldPrice = marketData?.goldPrice?.pricePerGramEUR || 87.45;
  const fxRates = marketData?.fxRates?.rates || { EUR: 1, USD: 1.08, GBP: 0.84, TRY: 38.5, AED: 3.97 };
  const isFallback = marketData?.goldPrice?.isFallback || marketData?.fxRates?.isFallback;

  return (
    <section id="calculator" className="py-16 md:py-24 bg-[#08080c]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-10 md:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-3">
            {t('investmentCalculator')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('calculateYourGold').split(' ').slice(0, -1).join(' ')} <span className="text-gradient-gold">{t('calculateYourGold').split(' ').slice(-1)}</span>
          </h2>
          <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto px-4">
            {t('calculatorSubtitle')}
          </p>
        </motion.div>

        {/* Instant Price Display */}
        <InstantPriceCard 
          goldPriceEUR={goldPrice} 
          fxRates={fxRates} 
          isLoading={isLoadingMarket}
          t={t}
        />

        {/* Quick Converter */}
        <QuickConverter
          goldPriceEUR={goldPrice}
          fxRates={fxRates}
          isLoading={isLoadingMarket}
          t={t}
        />

        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-200">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Calculator Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Card className="bg-[#12121a] border-[#1e1e2e] rounded-2xl overflow-hidden">
              <CardHeader className="border-b border-[#1e1e2e]">
                <CardTitle className="flex items-center gap-2 text-xl font-semibold text-white">
                  <Calculator className="w-5 h-5 text-[#D4AF37]" />
                  {t('inputYourDetails')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 p-4 md:p-6">
                {/* Amount Input */}
                <motion.div 
                  className="space-y-2"
                  whileFocus={{ scale: 1.01 }}
                >
                  <Label htmlFor="amount" className="text-sm font-medium text-gray-300">
                    {t('investmentAmount')}
                  </Label>
                  <Input
                    id="amount"
                    type="number"
                    placeholder={t('enterAmount')}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="h-12 text-lg bg-[#0a0a0f] border-[#1e1e2e] text-white placeholder:text-gray-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 transition-all"
                    min="0"
                    step="100"
                  />
                </motion.div>

                {/* Currency Select */}
                <div className="space-y-2">
                  <Label htmlFor="currency" className="text-sm font-medium text-gray-300">
                    {t('currency')}
                  </Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="h-12 bg-[#0a0a0f] border-[#1e1e2e] text-white">
                      <SelectValue placeholder={t('currency')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#12121a] border-[#1e1e2e]">
                      {CURRENCIES.map((curr) => (
                        <SelectItem key={curr.code} value={curr.code} className="text-white hover:bg-[#1e1e2e]">
                          <span className="flex items-center gap-2">
                            <span className="font-medium text-[#D4AF37]">{curr.symbol}</span>
                            <span>{curr.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Time Horizon Select */}
                <div className="space-y-2">
                  <Label htmlFor="horizon" className="text-sm font-medium text-gray-300">
                    {t('timeHorizon')}
                  </Label>
                  <Select value={horizon} onValueChange={setHorizon}>
                    <SelectTrigger className="h-12 bg-[#0a0a0f] border-[#1e1e2e] text-white">
                      <SelectValue placeholder={t('timeHorizon')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#12121a] border-[#1e1e2e]">
                      {HORIZONS.map((h) => (
                        <SelectItem key={h.value} value={h.value.toString()} className="text-white hover:bg-[#1e1e2e]">
                          {h.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Scenario Select */}
                <div className="space-y-2">
                  <Label htmlFor="scenario" className="text-sm font-medium text-gray-300">
                    {t('growthScenario')}
                  </Label>
                  <Select value={scenario} onValueChange={setScenario}>
                    <SelectTrigger className="h-12 bg-[#0a0a0f] border-[#1e1e2e] text-white">
                      <SelectValue placeholder={t('growthScenario')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#12121a] border-[#1e1e2e]">
                      {scenarios.map((s) => (
                        <SelectItem key={s.value} value={s.value} className="text-white hover:bg-[#1e1e2e]">
                          <span className="flex items-center gap-2">
                            <span>{s.label}</span>
                            <span className="text-[#D4AF37] text-sm">({s.rate})</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Calculate Button */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <motion.div className="flex-1" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      onClick={handleCalculate}
                      disabled={!amount || parseFloat(amount) <= 0 || isCalculating}
                      className="w-full h-12 btn-gold text-black font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCalculating ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          {t('calculate')}
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </motion.div>
                  
                  <AnimatePresence>
                    {result && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                      >
                        <Button
                          onClick={resetCalculator}
                          className="w-full sm:w-auto h-12 px-6 bg-transparent border border-[#1e1e2e] text-gray-300 hover:bg-[#1e1e2e] hover:text-white rounded-xl transition-all"
                        >
                          {t('reset')}
                        </Button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Current Rates Info */}
                <motion.div 
                  className="p-4 bg-[#0a0a0f] rounded-xl space-y-2 border border-[#1e1e2e]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Info className="w-4 h-4" />
                      <span className="font-medium">{t('currentMarketData')}</span>
                    </div>
                    {isFallback && (
                      <span className="text-xs px-2 py-1 bg-yellow-500/10 text-yellow-400 rounded border border-yellow-500/30">
                        {t('indicative')}
                      </span>
                    )}
                  </div>
                  {isLoadingMarket ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      {t('loadingMarketData')}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Gold (EUR/{t('gram')}):</span>
                        <span className="ml-2 font-medium text-white">€{goldPrice.toFixed(2)}</span>
                      </div>
                      {currency !== 'EUR' && fxRates[currency] && (
                        <div>
                          <span className="text-gray-500">FX ({currency}/EUR):</span>
                          <span className="ml-2 font-medium text-white">{fxRates[currency].toFixed(4)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Results Panel */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <AnimatePresence mode="wait">
              {result ? (
                <ResultsPanel result={result} resultRef={resultRef} t={t} />
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <EmptyState t={t} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default GoldCalculator;
